// Flutter Bridge for communication between Flutter WebView and Vue.js
class FlutterBridge {
  constructor() {
    this.isFlutter = false
    this.bridge = null
    this.init()
  }

  init() {
    // Check if running in Flutter WebView
    if (window.flutterBridge) {
      this.isFlutter = true
      this.bridge = window.flutterBridge
      console.log('Flutter bridge detected')
    } else {
      console.log('Running in browser mode')
    }
  }

  // Authentication methods
  async authenticateWithBiometrics() {
    if (!this.isFlutter) {
      console.warn('Biometric authentication only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.authenticateWithBiometrics()
    } catch (error) {
      console.error('Biometric authentication failed:', error)
      return false
    }
  }

  async authenticateWithFace() {
    if (!this.isFlutter) {
      console.warn('Face authentication only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.authenticateWithFace()
    } catch (error) {
      console.error('Face authentication failed:', error)
      return false
    }
  }

  async isBiometricAvailable() {
    if (!this.isFlutter) {
      console.warn('Biometric check only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.isBiometricAvailable()
    } catch (error) {
      console.error('Biometric availability check failed:', error)
      return false
    }
  }

  // Storage methods
  async setStorageValue(key, value) {
    if (!this.isFlutter) {
      console.warn('Secure storage only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.setStorageValue(key, value)
    } catch (error) {
      console.error('Storage set failed:', error)
      return false
    }
  }

  async getStorageValue(key) {
    if (!this.isFlutter) {
      console.warn('Secure storage only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getStorageValue(key)
    } catch (error) {
      console.error('Storage get failed:', error)
      return null
    }
  }

  // Camera methods
  async capturePhoto() {
    if (!this.isFlutter) {
      console.warn('Camera only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.capturePhoto()
    } catch (error) {
      console.error('Photo capture failed:', error)
      return null
    }
  }

  async captureVideo() {
    if (!this.isFlutter) {
      console.warn('Camera only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.captureVideo()
    } catch (error) {
      console.error('Video capture failed:', error)
      return null
    }
  }

  // QR Code scanning
  async scanQRCode() {
    if (!this.isFlutter) {
      console.warn('QR scanning only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.scanQRCode()
    } catch (error) {
      console.error('QR scanning failed:', error)
      return null
    }
  }

  // Location methods
  async getCurrentLocation() {
    if (!this.isFlutter) {
      console.warn('Location only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getCurrentLocation()
    } catch (error) {
      console.error('Location get failed:', error)
      return null
    }
  }

  async requestLocationPermission() {
    if (!this.isFlutter) {
      console.warn('Location permission only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.requestLocationPermission()
    } catch (error) {
      console.error('Location permission request failed:', error)
      return false
    }
  }

  // Notification methods
  async showNotification(title, body, data = {}) {
    if (!this.isFlutter) {
      console.warn('Notifications only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.showNotification(title, body, data)
    } catch (error) {
      console.error('Notification show failed:', error)
      return false
    }
  }

  async requestNotificationPermission() {
    if (!this.isFlutter) {
      console.warn('Notification permission only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.requestNotificationPermission()
    } catch (error) {
      console.error('Notification permission request failed:', error)
      return false
    }
  }

  // Device info methods
  async getDeviceInfo() {
    if (!this.isFlutter) {
      console.warn('Device info only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getDeviceInfo()
    } catch (error) {
      console.error('Device info get failed:', error)
      return null
    }
  }

  async getBatteryLevel() {
    if (!this.isFlutter) {
      console.warn('Battery info only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getBatteryLevel()
    } catch (error) {
      console.error('Battery level get failed:', error)
      return null
    }
  }

  // Network methods
  async getNetworkInfo() {
    if (!this.isFlutter) {
      console.warn('Network info only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getNetworkInfo()
    } catch (error) {
      console.error('Network info get failed:', error)
      return null
    }
  }

  // Check-in/out methods
  async checkIn(employeeId, remark = null, requireBiometric = false) {
    if (!this.isFlutter) {
      console.warn('Check-in only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.checkIn(employeeId, remark, requireBiometric)
    } catch (error) {
      console.error('Check-in failed:', error)
      return null
    }
  }

  async checkOut(employeeId, remark = null, requireBiometric = false) {
    if (!this.isFlutter) {
      console.warn('Check-out only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.checkOut(employeeId, remark, requireBiometric)
    } catch (error) {
      console.error('Check-out failed:', error)
      return null
    }
  }

  async getCheckInOutStatus(employeeId) {
    if (!this.isFlutter) {
      console.warn('Check-in/out status only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getCheckInOutStatus(employeeId)
    } catch (error) {
      console.error('Check-in/out status get failed:', error)
      return null
    }
  }

  // Attendance methods
  async getAttendanceHistory(employeeId, fromDate, toDate) {
    if (!this.isFlutter) {
      console.warn('Attendance history only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getAttendanceHistory(employeeId, fromDate, toDate)
    } catch (error) {
      console.error('Attendance history get failed:', error)
      return null
    }
  }

  // User profile methods
  async getUserProfile() {
    if (!this.isFlutter) {
      console.warn('User profile only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getUserProfile()
    } catch (error) {
      console.error('User profile get failed:', error)
      return null
    }
  }

  async updateUserProfile(profileData) {
    if (!this.isFlutter) {
      console.warn('User profile update only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.updateUserProfile(profileData)
    } catch (error) {
      console.error('User profile update failed:', error)
      return false
    }
  }

  // Auth token methods
  async getAuthToken() {
    if (!this.isFlutter) {
      console.warn('Auth token only available in Flutter app')
      return null
    }
    
    try {
      return await this.bridge.getAuthToken()
    } catch (error) {
      console.error('Auth token get failed:', error)
      return null
    }
  }

  async setAuthToken(token) {
    if (!this.isFlutter) {
      console.warn('Auth token set only available in Flutter app')
      return false
    }
    
    try {
      return await this.bridge.setAuthToken(token)
    } catch (error) {
      console.error('Auth token set failed:', error)
      return false
    }
  }

  // UI methods
  async showLoading(show = true) {
    if (!this.isFlutter) {
      console.warn('Loading UI only available in Flutter app')
      return
    }
    
    try {
      await this.bridge.showLoading(show)
    } catch (error) {
      console.error('Loading UI failed:', error)
    }
  }

  async showToast(message, type = 'info') {
    if (!this.isFlutter) {
      console.warn('Toast only available in Flutter app')
      return
    }
    
    try {
      await this.bridge.showToast(message, type)
    } catch (error) {
      console.error('Toast failed:', error)
    }
  }

  async showDialog(title, message, type = 'info') {
    if (!this.isFlutter) {
      console.warn('Dialog only available in Flutter app')
      return
    }
    
    try {
      return await this.bridge.showDialog(title, message, type)
    } catch (error) {
      console.error('Dialog failed:', error)
      return false
    }
  }

  // Navigation methods
  async navigateTo(route) {
    if (!this.isFlutter) {
      console.warn('Navigation only available in Flutter app')
      return
    }
    
    try {
      await this.bridge.navigateTo(route)
    } catch (error) {
      console.error('Navigation failed:', error)
    }
  }

  async goBack() {
    if (!this.isFlutter) {
      console.warn('Go back only available in Flutter app')
      return
    }
    
    try {
      await this.bridge.goBack()
    } catch (error) {
      console.error('Go back failed:', error)
    }
  }

  // Utility methods
  async sendToFlutter(method, data = null) {
    if (!this.isFlutter) {
      console.warn(`Method ${method} only available in Flutter app`)
      return null
    }
    
    try {
      if (data) {
        return await this.bridge[method](data)
      } else {
        return await this.bridge[method]()
      }
    } catch (error) {
      console.error(`Flutter method ${method} failed:`, error)
      return null
    }
  }

  // Check if running in Flutter
  isRunningInFlutter() {
    return this.isFlutter
  }

  // Get bridge instance
  getBridge() {
    return this.bridge
  }
}

// Create and export singleton instance
const flutterBridge = new FlutterBridge()

// Make it available globally
window.flutterBridge = flutterBridge

export default flutterBridge 